import { inject, Injectable } from '@angular/core';
import { SecureRedirectService } from '@app/core/services/secure-redirect/secure-redirect.service';
import { Params } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { EnvironmentService } from '@app/core/environment.service';
import { getDomainFromUrl } from '@app/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class RedirectsService {
  private secureRedirect = inject(SecureRedirectService);
  private httpClient = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  private readonly apimUrl = environment.apimAPIUrl;
  private readonly verifyRedirectDomainEndpoint =
    '/lifecycle/redirect_uri/verify';

  getGlobalRedirectURI(): string {
    return this.environmentService.getGlobalRedirectURI();
  }

  getSecuredCallbackUri(queryParams: Params): Observable<string | null> {
    const callBackUri = this.getCallBackUri(queryParams);
    return this.verifyRedirectDomainUri(callBackUri).pipe(
      map((isWhitelisted) => (isWhitelisted ? callBackUri : null)),
    );
  }

  getSecuredGoBackUri(queryParams: Params): Observable<string | null> {
    const callBackUri = this.getCallBackUri(queryParams);
    const origin = this.getOrigin(queryParams, callBackUri);
    return this.verifyRedirectDomainUri(origin).pipe(
      map((isWhitelisted) => (isWhitelisted ? origin : null)),
    );
  }

  getSecuredLoginRedirectUri(queryParams: Params): Observable<string | null> {
    const callBackUri = this.getCallBackUri(queryParams);
    const origin = this.getOrigin(queryParams, callBackUri);
    const loginRedirect = this.getLoginRedirectUri(queryParams, origin);
    return this.verifyRedirectDomainUri(loginRedirect).pipe(
      map((isWhitelisted) => (isWhitelisted ? loginRedirect : null)),
    );
  }

  addQueryParamsToUrl(url: string, queryParams: Params): string {
    let urlObj: URL;
    try {
      urlObj = new URL(url);
    } catch (error) {
      console.error('The uri provided is not a valid url');
      return '#';
    }
    Object.keys(queryParams).forEach((key) => {
      urlObj.searchParams.set(key, queryParams[key]);
    });
    return urlObj.toString();
  }

  private getCallBackUri(queryParams: Params): string | null {
    const redirect_uri = queryParams?.redirect_uri;
    const redirect_uri_after_registration =
      queryParams?.redirect_uri_after_registration;
    return this.secureRedirect.sanitizeUrl(
      redirect_uri_after_registration || redirect_uri,
    );
  }

  private getOrigin(queryParams: Params, callBackUri: string | null): string {
    let callBackUrlObj: URL;
    try {
      if (!callBackUri) {
        throw new Error('The callback uri is missing');
      }
      callBackUrlObj = new URL(callBackUri);
    } catch (error) {
      console.error('The callback uri provided is not a valid url');
      return '#';
    }
    return queryParams?.origin || callBackUrlObj.origin;
  }

  private getLoginRedirectUri(
    queryParams: Params,
    origin: string,
  ): string | null {
    const login_redirect_uri = queryParams?.login_redirect_uri;
    return this.secureRedirect.sanitizeUrl(
      login_redirect_uri || `${origin}/implicit/login`,
    );
  }

  private verifyRedirectDomainUri(uri: string | null): Observable<boolean> {
    try {
      if (!uri) {
        throw new Error('The redirect uri is missing');
      }
      new URL(uri);
    } catch (error) {
      console.error('The uri provided is not a valid url');
      return of(false);
    }

    const options = {
      headers: new HttpHeaders({
        'Ocp-Apim-Subscription-Key': environment.OcpApimSubscriptionKey,
      }),
    };
    return this.httpClient
      .post(
        this.apimUrl + this.verifyRedirectDomainEndpoint,
        { redirect_uri: getDomainFromUrl(uri) },
        options,
      )
      .pipe(
        map(() => true),
        catchError(() => {
          console.error('The redirect uri provided is not whitelisted');
          return of(false);
        }),
      );
  }
}
